import { Icon, SegmentedCard, Typography } from '@smartgift/frontend-toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AppLoader } from '@hero/atoms';
import { RECOGNITION_CONTACTS_TAB, RECOGNITION_CREATION_STEPS } from '@hero/constants';
import { useRecognitionCreationHeader } from '@hero/hooks';
import { PageLayout } from '@hero/layouts';
import { OnboardingCard } from '@hero/molecules';
import { ROUTE_PATHS } from '@hero/routes';
import { useUpdateRecognition } from '@hero/services/order-service';
import { useNewRecognitionStore, useOnboardingStore } from '@hero/store/hooks';
import { useValidateRules } from '@hero/store/hooks/useValidateRules';
import { getContactsTypeRule } from '@hero/validations';

import * as S from './style';

function NewRecognitionContactType() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { code, contacts, updateContacts } = useNewRecognitionStore();

  const [isPageLoading, setIsPageLoading] = React.useState(false);
  const header = useRecognitionCreationHeader({
    step: 2,
    setIsPageLoading
  });
  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();

  const onboarding = useOnboardingStore();

  const [isWithSendCard, setIsWithSendCard] = useState(true);
  const [isWithoutSendCard, setIsWithoutSendCard] = useState(false);
  const contactTypeOptions = [
    {
      icon: <Icon name="email" size="xl" />,
      title: (
        <Typography variant="body1SemiBold" color="accent" emphasis="primary-stronger">
          {t('weSendEmails')}
        </Typography>
      ),
      value: RECOGNITION_CONTACTS_TAB.list,
      hiddenInput: true,
      description: (
        <>
          <S.TabDescription as="div" variant="body2">
            {t('weSendEmailsDescription')}
            <S.TabDescriptionText as="div" variant="caption1">
              <Icon name="success" />
              {t('greatForHolidaysScheduledGifting')}
            </S.TabDescriptionText>
          </S.TabDescription>

          <OnboardingCard
            styles={{
              wrapper: {
                right: 34,
                top: 17
              }
            }}
            cardOffset={[18, 24]}
            position="right-start"
            isCardShown={isWithSendCard}
            title={t('selectFromContacts')}
            description={t('contactsOnboardingCard2Desc')}
            setIsCardShown={setIsWithSendCard}
            isInfoActive={onboarding?.recognition?.infoCardsActive}
            featureType="recognition"
            onTourButtonClick={() => {
              setIsWithoutSendCard(true);
            }}
          />
        </>
      )
    },
    {
      icon: <Icon name="link" size="xl" />,
      title: (
        <Typography variant="body1SemiBold" color="accent" emphasis="primary-stronger">
          {t('shareWithLink')}
        </Typography>
      ),
      value: RECOGNITION_CONTACTS_TAB.number,
      hiddenInput: true,
      description: (
        <>
          <S.TabDescription as="div" variant="body2">
            {t('shareWithALinkDescription')}
            <S.TabDescriptionText as="div" variant="caption1">
              <Icon name="success" px={16} space={2} />
              {t('greatForInMomentGiftingVirtualEvents')}
            </S.TabDescriptionText>
          </S.TabDescription>

          <OnboardingCard
            styles={{
              wrapper: {
                right: 34,
                top: 17
              }
            }}
            cardOffset={[18, -30]}
            position="right-end"
            isCardShown={isWithoutSendCard}
            title={t('sendWithoutContacts')}
            description={t('sendWithoutContactsOnboardingCardDesc')}
            setIsCardShown={setIsWithoutSendCard}
            onTourButtonClick={() => {}}
            isInfoActive={onboarding?.recognition?.infoCardsActive}
            featureType="recognition"
          />
        </>
      )
    }
  ];

  const { validate } = useValidateRules();

  const onNextButtonClick = async () => {
    const isValid = await validate([{ rule: getContactsTypeRule(), value: contacts?.type }]);
    if (!isValid) return;

    setIsPageLoading(true);

    try {
      await updateRecognition({
        params: {
          recognitionCode: code
        },
        body: {
          contacts,
          meta: {
            creationStep: RECOGNITION_CREATION_STEPS.newRecognitionRecipients
          }
        }
      });

      navigate(ROUTE_PATHS.NEW_RECOGNITION_RECIPIENTS);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    if (!code) {
      navigate(ROUTE_PATHS.MY_RECOGNITIONS);
    }
  }, []);

  return (
    <PageLayout
      header={header}
      navigation={{
        rightButton: {
          children: t('next'),
          onClick: onNextButtonClick,
          loading: isLoadingUpdateRecognition,
          icon: true
        },
        leftButton: {
          children: t('back'),
          onClick: () => navigate(ROUTE_PATHS.NEW_RECOGNITION_GIFTS)
        }
      }}
    >
      <S.Wrapper>
        <Typography variant="subtitle4Bold">{t('howWouldYouLikeToShare')}</Typography>
        <S.Content>
          <SegmentedCard
            updateHash={false}
            activeTab={contacts?.type}
            onChange={async type => {
              await updateContacts({
                ...contacts,
                type
              });
            }}
            options={contactTypeOptions}
          />
        </S.Content>
      </S.Wrapper>
      {isPageLoading && <AppLoader />}
    </PageLayout>
  );
}

export { NewRecognitionContactType };
